import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getAmbulanceQueue,
  getAmbulanceServiceProfile,
  setAmbulanceServiceLogo,
} from '../../api/ambulance.js'
import { Card, CardHeader } from '../../components/Card.jsx'
import CatalogueImageThumb from '../../components/CatalogueImageThumb.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import QueueTabs, { makeClassifier } from '../../components/QueueTabs.jsx'
import { formatDateTime, statusColor } from '../../utils/format.js'
import { useAuth } from '../../auth/AuthProvider.jsx'

// Ambulance dispatches are "done" once completed or cancelled. Anything
// in flight (requested, dispatched, en_route, arrived) belongs in New
// because the dispatcher / crew may still need to act on it. The
// crucial property — emergency-priority items must always appear in
// New until terminal — falls out of this taxonomy automatically since
// emergencies don't get terminal statuses while still in motion.
const classifyAmbulanceRequest = makeClassifier({
  doneStatuses: ['completed', 'cancelled', 'archived'],
})

/**
 * Ambulance queue — dispatch requests routed to this service. Urgency is
 * surfaced directly on the row since it drives dispatcher prioritisation.
 */
export default function AmbulanceDashboard() {
  const q = useQuery({
    queryKey: ['ambulance-queue'],
    queryFn: getAmbulanceQueue,
    refetchInterval: 10_000,
  })

  const urgencyColor = (u) => {
    const v = (u || '').toLowerCase()
    if (v === 'emergency') return 'border-rose-300 text-rose-700 bg-rose-50'
    if (v === 'priority') return 'border-amber-300 text-amber-700 bg-amber-50'
    return 'border-slate-200 text-slate-600'
  }

  return (
    <div className="space-y-4">
      <ServiceBrandingCard />
      <Card>
        <CardHeader
          title="Ambulance dispatch"
          subtitle="Requests routed to your service"
          right={
            <button onClick={() => q.refetch()} className="text-xs text-wantok-700 underline">Refresh</button>
          }
        />
        {q.error && <ErrorBanner error={q.error} onRetry={() => q.refetch()} />}
        {q.isLoading ? (
          <div className="py-3 text-sm text-slate-400">Loading…</div>
        ) : (
          <QueueTabs
            rows={q.data || []}
            classifyRow={classifyAmbulanceRequest}
            keyExtractor={(r) => r.id}
            emptyMessages={{
              new: 'No active dispatch requests.',
              processed: 'No completed dispatches in the last 30 days.',
              archived: 'No older dispatches in the archive.',
            }}
            renderRow={(r) => {
              const patient = r.patient_name || 'Unknown patient'
              const isPatientInitiated = r.initiated_by === 'patient'
              return (
                <li
                  key={r.id}
                  className={
                    'py-2 flex items-center justify-between gap-2 ' +
                    // Subtle amber left border on patient-initiated rows
                    // so the dispatcher's eye is drawn to the ones that
                    // skipped a doctor pre-screen and may need a triage
                    // call before dispatch.
                    (isPatientInitiated
                      ? 'border-l-2 border-amber-400 pl-2 -ml-2'
                      : '')
                  }
                >
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-800 truncate">{patient}</div>
                    <div className="text-sm text-slate-700 truncate">{r.service_type || 'Transport'}</div>
                    <div className="text-xs text-slate-500 flex flex-wrap gap-2 mt-0.5">
                      <span className={`uppercase border rounded-full px-2 py-0.5 text-[10px] ${statusColor(r.status)}`}>{r.status}</span>
                      <span className={`uppercase border rounded-full px-2 py-0.5 text-[10px] ${urgencyColor(r.urgency)}`}>{r.urgency}</span>
                      {isPatientInitiated && (
                        <span
                          className="uppercase border rounded-full px-2 py-0.5 text-[10px] bg-amber-50 border-amber-300 text-amber-800"
                          title="Patient submitted this request directly — there's no doctor pre-screen, so triage by phone before dispatching."
                        >
                          via patient
                        </span>
                      )}
                      <span>Requested {formatDateTime(r.created_at)}</span>
                      {r.patient_phone && <span>· ☎ {r.patient_phone}</span>}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1 truncate">
                      {r.pickup_location}{r.destination ? ` → ${r.destination}` : ''}
                    </div>
                  </div>
                  <Link to={`/ambulance/requests/${r.id}`} className="text-xs text-wantok-700 underline">Open</Link>
                </li>
              )
            }}
          />
        )}
      </Card>
    </div>
  )
}

/**
 * Branding card — lets the dispatcher upload a service logo / vehicle
 * photo for the patient-facing marketplace and dispatch surfaces. Hidden
 * for ambulance staff who aren't yet linked to a service (admin must
 * bind via Django admin → Ambulance services → Ambulance staff links).
 */
function ServiceBrandingCard() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const isAdmin = Boolean(user?.is_staff || user?.is_superuser)
  const profileId = user?.ambulance_profile?.id ?? null
  const serviceName = user?.ambulance_profile?.name || 'My ambulance service'

  const profileQ = useQuery({
    queryKey: ['ambulance-service-profile', profileId],
    queryFn: () => getAmbulanceServiceProfile(profileId),
    enabled: Boolean(profileId),
  })

  if (!profileId) {
    // Admins or unlinked staff don't get the branding card here. Admins
    // edit ambulance services via Django admin; unlinked staff need to
    // be bound to a service first.
    return null
  }

  const logoUrl = profileQ.data?.logo_url || null

  return (
    <Card>
      <CardHeader
        title="Service branding"
        subtitle="Logo or vehicle photo shown to patients on the marketplace and on dispatch surfaces"
      />
      {profileQ.error && <ErrorBanner error={profileQ.error} />}
      <div className="flex items-center gap-4">
        <CatalogueImageThumb
          url={logoUrl}
          alt={serviceName}
          editable={true}
          size={64}
          onUpload={async (file) => {
            await setAmbulanceServiceLogo(profileId, file)
            qc.invalidateQueries({ queryKey: ['ambulance-service-profile', profileId] })
          }}
          onClear={async () => {
            await setAmbulanceServiceLogo(profileId, null)
            qc.invalidateQueries({ queryKey: ['ambulance-service-profile', profileId] })
          }}
        />
        <div className="text-sm">
          <div className="font-semibold text-slate-800">{serviceName}</div>
          <div className="text-xs text-slate-500">
            {logoUrl
              ? 'Click the logo to replace or remove it.'
              : 'Click the placeholder to upload your service logo or a vehicle photo.'}
          </div>
        </div>
      </div>
    </Card>
  )
}
